import { Toolbar } from '@bynder/design-system';
import {
  IconBold,
  IconClearFormatting,
  IconItalic,
  IconListBulleted,
  IconListNumbered,
  IconSingleSparkle,
  IconStrikeThrough,
  IconUnderline,
} from '@bynder/icons';
import { publish } from '@bynder/micro-frontend-communication';
import { posToDOMRect } from '@tiptap/core';
import { useEffect, useRef, useState } from 'react';
import TextStyleOptionsDropdown from '../../components/text-style-dropdown/text-style-options-dropdown.tsx';
import { useTeaContext } from '../../contexts/tea-context.tsx';
import type { EditorFormats } from '../../data/.types/editorFormats.ts';
import {
  type EditorOptions,
  defaultEditorOptions,
} from '../../data/.types/editorOptions.ts';
import { getValueOfCssVariable } from '../../helpers/dom.ts';
import { ColorPickerDropdown } from '../color-picker/color-picker-dropdown.tsx';
import Feature from '../feature.tsx';
import { LinkEditor } from '../link-editor/link-editor.tsx';
import { clearFormats } from './clearFormats.ts';

type FormatsToolbarProps = {
  editorFormats?: EditorFormats;
  editorOptions?: EditorOptions;
  baseUrl?: string;
  accessToken?: string;
};

export const FormatsToolbar = ({
  editorFormats,
  editorOptions = defaultEditorOptions,
}: FormatsToolbarProps) => {
  const { editor } = useTeaContext();

  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({
    top: '0px',
  });
  const [editorHasSelection, setEditorHasSelection] = useState(false);

  const toolbarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editor) return;

    const followSelection = editorOptions?.toolbar?.followSelection;
    const showOnSelection = editorOptions?.toolbar?.showOnSelection;

    const updateMenu = () => {
      const hasSelection = !editor.state.selection.empty;
      setEditorHasSelection(hasSelection);

      if (!editor.isFocused || (showOnSelection && !editorHasSelection)) {
        setIsVisible(false);
        return;
      }

      const toolbarHeight = toolbarRef.current?.clientHeight || 0;
      const toolbarVerticalMargin = 10;

      // The editor rect, relative to the viewport
      const editorRect = posToDOMRect(editor.view, 0, 0);
      // The selection rect, relative to the viewport
      const selectionRect = posToDOMRect(
        editor.view,
        editor.state.selection.from,
        editor.state.selection.to,
      );

      let defaultTopPosition = toolbarHeight + toolbarVerticalMargin;
      const selectionTopPosition = selectionRect.top - editorRect.top;

      // CXUC styling
      // LI are used to list all the widgets in the page
      // Including the data attribute to make sure it's not just a random LI
      const closestLIWrapper = editor.view.dom.closest(
        'li[data-rbd-draggable-id]',
      );

      if (closestLIWrapper) {
        // if there's top padding, it's not the first LI in the widget list
        const closestLIWrapperPaddingTop =
          window.getComputedStyle(closestLIWrapper).paddingTop;

        // The default value in 99% of cases is 16px, so this double checks we're in the right place
        if (closestLIWrapperPaddingTop === '16px') {
          defaultTopPosition += 16; // Adjust for the padding-top of 16px
        }
      }

      const calculatedTopPosition =
        followSelection && editorHasSelection
          ? 0 - defaultTopPosition + selectionTopPosition
          : 0 - defaultTopPosition;

      setPosition({
        top: `${calculatedTopPosition}px`,
      });

      setIsVisible(true);
    };

    const handleBlur = ({ event }: { event: FocusEvent }) => {
      // Check if the new focus target is inside our menu
      if (
        event?.relatedTarget &&
        toolbarRef.current?.parentNode?.contains(event.relatedTarget as Node)
      ) {
        return;
      }

      setIsVisible(false);
    };

    const handleOutsideClick = (event: MouseEvent) => {
      const eventTargetInsideToolbarOrEditor =
        event?.target &&
        (toolbarRef.current?.contains(event.target as Node) ||
          editor.view.dom.contains(event.target as Node));

      const editorIsFocusedOrHasSelection =
        editor.isFocused || !editor.state.selection.empty;

      if (eventTargetInsideToolbarOrEditor || editorIsFocusedOrHasSelection) {
        return;
      }

      setIsVisible(false);
    };

    // Update on selection change and focus
    editor.on('selectionUpdate', updateMenu);
    editor.on('focus', updateMenu);
    editor.on('blur', handleBlur);
    document.addEventListener('click', handleOutsideClick);

    // Initial position
    updateMenu();

    return () => {
      editor.off('selectionUpdate', updateMenu);
      editor.off('focus', updateMenu);
      editor.off('blur', handleBlur);
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [editor, editorOptions, editorHasSelection]);

  useEffect(() => {
    const tipTapEditor = editor?.view.dom;

    if (!tipTapEditor) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'M' && event.ctrlKey && event.shiftKey) {
        toolbarRef.current?.focus();
      }
    };

    tipTapEditor.addEventListener('keydown', handleKeyDown);

    return () => {
      tipTapEditor.removeEventListener('keydown', handleKeyDown);
    };
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <>
      <div
        aria-live="polite"
        className="visually-hidden tea-formats-toolbar-live-status"
      >
        {isVisible && editor.isFocused
          ? 'Formatting toolbar is open. Press Control + Shift + M to enter the toolbar.'
          : 'Formatting toolbar is closed'}
      </div>
      <div
        // biome-ignore lint/a11y/noNoninteractiveTabindex: this is a floating menu
        tabIndex={0}
        role="toolbar"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            editor.chain().focus().run;
          }
        }}
        className="tea-formats-toolbar"
        ref={toolbarRef}
        data-testid="formats-toolbar"
        style={{
          top: `${position.top}`,
          visibility: isVisible ? 'visible' : 'hidden', // we have to use visibility instead of display otherwise the the ref (which we obtain the height from) will be null
        }}
      >
        <Toolbar>
          <TextStyleOptionsDropdown
            editor={editor}
            textStyles={editorFormats?.textStyles}
          />
          <Toolbar.Divider />
          <ColorPickerDropdown
            editor={editor}
            onChange={(color): void => {
              editor
                .chain()
                .focus()
                .setColor(getValueOfCssVariable(color.value) ?? color.value)
                .updateAttributes('link', { color: color.value })
                .run();
            }}
            colorOptions={editorFormats?.colors}
          />
          <Toolbar.Button
            aria-label="Bold"
            aria-pressed={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
            isPressed={editor.isActive('bold')}
          >
            <IconBold />
          </Toolbar.Button>
          <Toolbar.Button
            aria-label="Underline"
            aria-pressed={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isPressed={editor.isActive('underline')}
          >
            <IconUnderline />
          </Toolbar.Button>{' '}
          <Toolbar.Button
            aria-label="Italic"
            aria-pressed={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isPressed={editor.isActive('italic')}
          >
            <IconItalic />
          </Toolbar.Button>{' '}
          <Toolbar.Button
            aria-label="Strike through"
            aria-pressed={editor.isActive('strike')}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            isPressed={editor.isActive('strike')}
          >
            <IconStrikeThrough />
          </Toolbar.Button>
          <Toolbar.Button
            aria-label="Clear formatting from selection"
            onClick={() => clearFormats(editor)}
            isDisabled={!editorHasSelection}
          >
            <IconClearFormatting />
          </Toolbar.Button>
          <Toolbar.Divider />
          <LinkEditor editor={editor} />
          <Toolbar.Divider />
          <Toolbar.Button
            aria-pressed={editor.isActive('bulletList')}
            aria-label="Bullet list"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isPressed={editor.isActive('bulletList')}
          >
            <IconListBulleted />
          </Toolbar.Button>
          <Toolbar.Button
            aria-pressed={editor.isActive('orderedList')}
            aria-label="Numbered list"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isPressed={editor.isActive('orderedList')}
          >
            <IconListNumbered />
          </Toolbar.Button>
          <Feature name="cw_conversational_ai_cxuc">
            <>
              <Toolbar.Divider />
              <Toolbar.Button
                aria-label="Toggle Conversational AI sidebar"
                onClick={() => publish('conversational-ai/toggle-sidebar')}
              >
                <IconSingleSparkle />
              </Toolbar.Button>
            </>
          </Feature>
        </Toolbar>
      </div>
    </>
  );
};
